// src/components/categories/CategoryList.jsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Collapse, 
  IconButton, 
  Divider,
  Button,
  useTheme,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import CategoryCard from './CategoryCard';
import Loader from '../common/Loader';
import { useCategories } from '../../context/CategoryContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import PERMISSIONS from '../../config/permissionsList'; // Ajustez le chemin si nécessaire
import { useTranslation } from 'react-i18next';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';
import { common } from '@mui/material/colors';
// URL de l'API (à remplacer par votre URL réelle)
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';

const CategoryList = ({ onEdit, onDelete, isAdmin, isOwner, categories: propCategories }) => {
  
  const theme = useTheme();
  const { t } = useTranslation();
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const { categories: contextCategories, loading, error } = useCategories();
  const { user } = useAuth();
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Utiliser les catégories passées en props ou celles du contexte
  const categories = propCategories || contextCategories;
  
  // Utiliser le hook usePermission pour vérifier les permissions
  const { hasPermission: canViewCategories } = usePermission(PERMISSIONS.VIEW_CATEGORIES );
  const { hasPermission: canUpdateCategory } = usePermission(PERMISSIONS.CATEGORY_UPDATE );
  const { hasPermission: canDeleteCategory } = usePermission(PERMISSIONS.CATEGORY_DELETE );

  // Fonction pour construire l'URL de l'image avec un timestamp pour éviter le cache
  const getCategoryImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Si l'URL est déjà absolue (commence par http ou https)
    if (imageUrl.startsWith('http')) {
      return `${imageUrl}?t=${new Date().getTime()}`;
    }
    
    // Sinon, construire l'URL complète
    return `${API_URL}/${imageUrl}?t=${new Date().getTime()}`;
  };

  // Ajouter des logs pour déboguer
  console.log('CategoryList props:', { onEdit, onDelete, isAdmin, isOwner });
  console.log('User:', user);
  console.log('Categories:', categories);
  console.log('Permissions:', { 
    canViewCategories, 
    canUpdateCategory, 
    canDeleteCategory 
  });

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  if (loading) return <Loader />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!categories || categories.length === 0) {
    return <Typography>{t('categoryManagement.noCategoriesFound')}</Typography>;
  }

  // Si l'utilisateur n'a pas la permission de voir les catégories
  if (!canViewCategories) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {t('categoryManagement.noPermissionCategoriesView')}
        </Typography>
      </Box>
    );
  }

  // Animations pour les catégories
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {categories.map((category) => {
          // Logs pour déboguer chaque catégorie
          console.log('Rendering category:', category);
          
          // Déterminer si l'utilisateur peut modifier cette catégorie
          // Soit par permission, soit par règle métier (propriétaire)
          const canEdit = canUpdateCategory || (isOwner && category.createdBy === user?.id);
          
          // Déterminer si l'utilisateur peut supprimer cette catégorie
          // Soit par permission, soit par règle métier (admin)
          const canDelete = canDeleteCategory || (isAdmin);
          
          console.log('Can edit?', canEdit);
          console.log('Can delete?', canDelete);
          
          return (
            <motion.div key={category.id} variants={itemVariants}>
              <Box 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                    borderColor: theme.palette.primary.light
                  }
                }}
              >
                {/* Contenu principal de la catégorie en layout horizontal */}
                <Box sx={{ display: 'flex', minHeight: 200 }}>
                  {/* Image de la catégorie */}
                  <Box sx={{ width: 300, flexShrink: 0 }}>
                    {category.imageUrl ? (
                      <Box 
                        component="img"
                        src={getCategoryImageUrl(category.imageUrl)}
                        alt={category.name}
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Error loading image:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    
                    {/* Fallback pour les images manquantes */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: category.imageUrl ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: `${theme.palette.primary.main}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                          }}
                        >
                          <CategoryIcon 
                            sx={{ 
                              fontSize: 40, 
                              color: theme.palette.primary.main 
                            }} 
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Aucune image
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Contenu textuel */}
                  <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between',
                        mb: 2
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          flexGrow: 1
                        }}
                        onClick={() => toggleCategory(category.id)}
                      >
                        <Typography variant="h5" component="h2" sx={{ 
                          fontWeight: 600,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          mr: 1
                        }}>
                          {getCategoryName(category)}
                        </Typography>
                        {category.children && category.children.length > 0 && (
                          <IconButton size="small">
                            {expandedCategories[category.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </Box>
                      
                      {/* Boutons d'action */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {onEdit && canEdit && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(category);
                            }}
                            startIcon={<EditIcon />}
                          >                        
                            {t('common.edit')}
                          </Button>
                        )}
                        
                        {onDelete && canDelete && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(category);
                            }}
                            startIcon={<DeleteIcon />}
                          >
                            {t('common.delete')}
                          </Button>
                        )}                   
                      </Box>
                    </Box>

                    {/* Description */}
                    {category.description && (
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2, 
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          flex: 1
                        }}
                      >
                        {getCategoryDescription(category)}
                      </Typography>
                    )}

                    {/* Badges */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                      {category.children && category.children.length > 0 && (
                        <Chip 
                          size="small" 
                          label={`${category.children.length} ${category.children.length > 1 ? t('categories.subCategoriesCountPlural') : t('categories.subCategoriesCount')}`}
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: 24,
                            borderRadius: '12px'
                          }}
                        />
                      )}
                    </Box>

                    {/* Afficher un badge si la catégorie est en attente d'approbation */}
                    {user?.isAdmin && category.pendingApproval && (
                      <Box sx={{ mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: 'inline-block',
                            bgcolor: 'warning.light', 
                            color: 'warning.contrastText',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          {t('common.pendingValidation')}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Afficher un badge si la catégorie a été créée par l'utilisateur actuel */}
                    {isOwner && category.createdBy === user?.id && (
                      <Box sx={{ mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: 'inline-block',
                            bgcolor: 'info.light', 
                            color: 'info.contrastText',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                            {t('common.CreatedByYou')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                
                {category.children && category.children.length > 0 && (
                  <Collapse in={expandedCategories[category.id] || false}>
                    <Box sx={{ px: 3, pb: 3 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                        {t('categories.subcategories')}
                      </Typography>
                      <Grid container spacing={2}>
                        {category.children.map((child) => (
                          <Grid item xs={12} sm={6} md={4} key={child.id}>
                            <CategoryCard 
                              category={child} 
                              onEdit={canEdit ? onEdit : undefined}
                              onDelete={canDelete ? onDelete : undefined}
                              getCategoryImageUrl={getCategoryImageUrl}
                              elevation={0}
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                  borderColor: theme.palette.primary.main
                                }
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Collapse>
                )}
              </Box>
            </motion.div>
          );
        })}
      </motion.div>
    </Box>
  );
};

export default CategoryList;